import React, { useState, useEffect } from "react";
import "./App.css";
import AddressGenerator from "./components/AddressGenerator";
import MessageSigner from "./components/MessageSigner";
import DepositSigner from "./components/DepositSigner";
import AgentDelegation from "./components/AgentDelegation";
import StatusPanel from "./components/StatusPanel";
import SDKTestPage from "./test_pages/SDKTestPage";
import WebSocketTestPage from "./test_pages/WebSocketTestPage";
import LayoutTestPage from "./test_pages/LayoutTestPage";
import HooksTestPage from "./test_pages/HooksTestPage";
import TradingTestPage from "./test_pages/TradingTestPage";
import { useMormSigner } from "./hooks";
import { AppProvider } from "./context/AppContext";
import { Toaster } from "./components/ui/Toaster";

type Page =
  | "main"
  | "sdk-test"
  | "websocket-test"
  | "layout-test"
  | "hooks-test"
  | "trading-test";

function App(): JSX.Element {
  const { wasmStatus, isReady } = useMormSigner();
  const [currentPage, setCurrentPage] = useState<Page>("main");

  // Listen for custom event to navigate to websocket test page
  useEffect(() => {
    const handleNavigate = () => {
      setCurrentPage("websocket-test");
    };
    window.addEventListener("navigateToWebSocketTest", handleNavigate);
    return () => {
      window.removeEventListener("navigateToWebSocketTest", handleNavigate);
    };
  }, []);

  return (
    <AppProvider>
      <div className="App">
        <header className="App-header">
          <h1>Morm SDK</h1>
          <p>
            Test WebAssembly integration for morm_signer with Agent Delegation
          </p>

          {/* Navigation */}
          <nav style={{ marginTop: "1rem", display: "flex", gap: "1rem" }}>
            <button
              onClick={() => setCurrentPage("main")}
              style={{
                padding: "0.5rem 1rem",
                backgroundColor: currentPage === "main" ? "#007bff" : "#6c757d",
                color: "white",
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
              }}
            >
              Morm Signer
            </button>
            <button
              onClick={() => setCurrentPage("sdk-test")}
              style={{
                padding: "0.5rem 1rem",
                backgroundColor:
                  currentPage === "sdk-test" ? "#007bff" : "#6c757d",
                color: "white",
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
              }}
            >
              SDK Trade API
            </button>
            <button
              onClick={() => setCurrentPage("websocket-test")}
              style={{
                padding: "0.5rem 1rem",
                backgroundColor:
                  currentPage === "websocket-test" ? "#007bff" : "#6c757d",
                color: "white",
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
              }}
            >
              WebSocket Test
            </button>
            <button
              onClick={() => setCurrentPage("layout-test")}
              style={{
                padding: "0.5rem 1rem",
                backgroundColor:
                  currentPage === "layout-test" ? "#007bff" : "#6c757d",
                color: "white",
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
              }}
            >
              Layout Test
            </button>
            <button
              onClick={() => setCurrentPage("hooks-test")}
              style={{
                padding: "0.5rem 1rem",
                backgroundColor:
                  currentPage === "hooks-test" ? "#007bff" : "#6c757d",
                color: "white",
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
              }}
            >
              Hooks Test
            </button>
            <button
              onClick={() => setCurrentPage("trading-test")}
              style={{
                padding: "0.5rem 1rem",
                backgroundColor:
                  currentPage === "trading-test" ? "#007bff" : "#6c757d",
                color: "white",
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
              }}
            >
              Trading Test
            </button>
          </nav>
        </header>

        <main className="App-main">
          {currentPage === "sdk-test" ? (
            <SDKTestPage />
          ) : currentPage === "websocket-test" ? (
            <WebSocketTestPage />
          ) : currentPage === "layout-test" ? (
            <LayoutTestPage />
          ) : currentPage === "hooks-test" ? (
            <HooksTestPage />
          ) : currentPage === "trading-test" ? (
            <TradingTestPage />
          ) : (
            <>
              <StatusPanel status={wasmStatus} />

              {isReady && (
                <div className="App-content">
                  <div className="App-section">
                    <AddressGenerator />
                  </div>

                  <div className="App-section">
                    <MessageSigner />
                  </div>

                  <div className="App-section">
                    <DepositSigner />
                  </div>

                  <div className="App-section">
                    <AgentDelegation />
                  </div>
                </div>
              )}

              {wasmStatus.error && (
                <div className="App-error">
                  <h3>Error Loading WASM</h3>
                  <p>{wasmStatus.error}</p>
                  <p>
                    Make sure the morm_signer.wasm file is available in the
                    public directory.
                  </p>
                </div>
              )}
            </>
          )}
        </main>
      </div>
      <Toaster
        position="bottom-right"
        toastOptions={{
          duration: 4000,
          style: {
            color: "var(--foreground)",
            border: "1px solid var(--border)",
          },
        }}
      />
    </AppProvider>
  );
}

export default App;
